import type {
  ItemId,
  WheelActionCard,
  WheelLedgerLine,
  WheelObservableState,
  WheelSeatView,
  WheelWedge,
} from '@goodtime/shared';

/**
 * Pure logic for the central ledger — no Pixi imports so the
 * functions stay testable in plain Node. The widget in
 * {@link ./WheelResolveCentralLedger} re-exports these so callers
 * only need one import path. Eventually this ledger math will move
 * server-side (producer note 2026-05-27) and these helpers become
 * the client-side renderer of a server-built payload.
 */

export interface CentralLedgerLineItem {
  /** Label, e.g. "Bea's Bomb" (incoming) or "Hit Bea" (outgoing). */
  label: string;
  /** Signed delta — green when positive, red when negative. */
  delta: number;
  /** True for SHIELD-absorbed sabotage so the row shows a muted
   *  "blocked" tag instead of a zero. */
  blocked?: boolean;
  /** Muted words shown in place of the number, e.g. "nothing to
   *  steal" for a Shakedown on a rival who won nothing. */
  note?: string;
}

/** A Shakedown whose target won nothing this spin: the server still
 *  sends its card (and ledger line) with both deltas at 0, so the item
 *  never vanishes unexplained (producer 2026-11-03). */
export function isNothingToSteal(card: Pick<WheelActionCard, 'itemId' | 'blocked' | 'sourceDelta' | 'targetDelta'>): boolean {
  return card.itemId === 'SHAKEDOWN' && !card.blocked && card.sourceDelta === 0 && card.targetDelta === 0;
}

/** The note a zero-delta Shakedown line carries, by side. */
function shakedownNote(itemId: ItemId, delta: number, blocked: boolean, isSource: boolean): string | undefined {
  if (itemId !== 'SHAKEDOWN' || delta !== 0 || blocked) return undefined;
  return isSource ? 'nothing to steal' : 'nothing taken';
}

/** Items that shift the wedge/multiplier itself, so their effect is
 *  already in the stake row's "stake × multiplier" subtotal and they
 *  must NOT appear as additive line items (listing them double-counts).
 *
 *  X2_BONUS is deliberately NOT here: unlike the others it's a
 *  post-resolve doubling that the server emits as its own ledger line
 *  with a clean additive delta (post − pre payout), so it surfaces as a
 *  "×2 Bonus +N" row and {@link reconcileLedgerSubtotal} peels it back
 *  out of the subtotal — the sum is unchanged (producer 2026-06-10:
 *  "the double coin should show on the ledger"). Mirrors the server's
 *  WEDGE_BAKED_ITEMS in WheelDialResolver; flip both sides + tests.
 *
 *  Producer feedback 2026-05-27: "Magnet technically doesn't add
 *  anything. It improves the user's multiplier." Same for BOOST /
 *  TRIPLE_BOOST.
 */
export const WEDGE_BAKED_ITEMS: ReadonlySet<ItemId> = new Set([
  'MAGNET',
  'BOOST',
  'TRIPLE_BOOST',
]);

const ITEM_LABEL: Partial<Record<ItemId, string>> = {
  HIT: 'Hit',
  BOMB: 'Bomb',
  POW: 'POW',
  LIGHTNING: 'Lightning',
  SHAKEDOWN: 'Shakedown',
  MAGNET: 'Magnet',
  BOOST: 'Boost',
  TRIPLE_BOOST: '3× Boost',
  X2_BONUS: '×2 Bonus',
  COIN_STASH: 'Coin Stash',
  SHIELD: 'Shield',
  BONUS_WEDGE: 'Bonus Wedge',
  GEM_DROP: 'Gem Drop',
  HEIST: 'Heist',
  CRAZY_EIGHT: 'Crazy 8',
  GOLD_RUSH: 'Gold Rush',
};

/** Format the stake row's left-hand label. The "×" is the
 *  multiplication operator — there must be exactly one. (An
 *  earlier version rendered "10 × ×2" by accident.)
 */
export function formatWedge(wedge: WheelWedge, stake: number): string {
  switch (wedge.kind) {
    case 'COINS':
      return `${stake} × ${wedge.multiplier ?? 0}`;
    case 'BANKRUPT':
      return `${stake} × BUST`;
    case 'SKIP_NEXT':
      return `${stake} · SKIP NEXT`;
    case 'EXTRA_ITEM':
      return `${stake} · ITEM`;
    case 'SPECIAL_PRIZE':
      return `${stake} · BIG PRIZE`;
  }
}

/** Items whose ledger delta is a SETTLEMENT-time grant — credited on
 *  top of the spin payout at PAYOUT, NOT baked into
 *  {@code perSeatOutcome.payout}. COIN_STASH (+50) is the only one in
 *  v1. They must NOT be peeled out when reconciling the stake-row
 *  subtotal (see {@link reconcileLedgerSubtotal}); doing so would drop
 *  them from the WINNINGS total. */
const SETTLEMENT_ADD_ITEMS: ReadonlySet<ItemId> = new Set(['COIN_STASH']);

/**
 * Reconcile the stake-row subtotal so the central ledger's rows sum
 * to the player's TRUE take and can never read negative.
 *
 * <p>The widget renders {@code WINNINGS = subtotal + Σ(line deltas)}.
 * Setting {@code subtotal = perSeatOutcome.payout} (the final,
 * all-effects-baked value) double-counts every shown line — a steal
 * or a heavy sabotage shift then drives WINNINGS negative even though
 * the player kept a non-negative payout (e.g. landed "×3 = 1", then
 * "Lightning −2" → "WINNINGS −1"). Instead, peel the baked lines back
 * out so the stake row shows the BASE spin result:
 *
 * <pre>   subtotal = max(0, payout) − Σ(baked line deltas)</pre>
 *
 * Every line EXCEPT settlement-time grants ({@link
 * SETTLEMENT_ADD_ITEMS}) is already baked into {@code payout}, so:
 *
 * <pre>   WINNINGS = subtotal + Σ(all lines)
 *            = max(0, payout) + Σ(settlement-add lines)   ≥ 0</pre>
 *
 * The server already floors {@code payout ≥ 0} and bounds steals to
 * the prize layer; the {@code max(0,…)} here is belt-and-braces for
 * legacy SSE shapes.
 */
export function reconcileLedgerSubtotal(
  payout: number,
  lines: ReadonlyArray<WheelLedgerLine>,
): number {
  let bakedLineSum = 0;
  for (const l of lines) {
    if (!SETTLEMENT_ADD_ITEMS.has(l.itemId)) bakedLineSum += l.delta;
  }
  return Math.max(0, payout) - bakedLineSum;
}

/**
 * Stake-row label. Derives the effective multiplier from the
 * (reconciled) base {@code subtotal} so the row is internally
 * consistent — the shown "stake × N" always equals the subtotal,
 * even when incoming sabotage knocked the player off the wedge their
 * FINAL {@code wedge} reflects (which would otherwise read e.g.
 * "100 × 2 = 500"). Falls back to the raw wedge label when the
 * subtotal isn't a clean multiple of the stake (zero stake, a
 * bust/effect wedge, or a flat-tax remainder).
 */
export function formatStakeRow(
  stake: number,
  subtotal: number,
  wedge: WheelWedge,
): string {
  if (
    wedge.kind === 'COINS' &&
    stake > 0 &&
    subtotal >= 0 &&
    subtotal % stake === 0
  ) {
    return `${stake} × ${subtotal / stake}`;
  }
  return formatWedge(wedge, stake);
}

/** Build the line-item list for the local player from the action
 *  cards in this match's resolve result. Cards that don't touch the
 *  local seat are skipped. Per-rival sibling cards (LIGHTNING/POW)
 *  are skipped so the player sees ONE summary line per item, not N.
 *  Wedge-baked items (see {@link WEDGE_BAKED_ITEMS}) are skipped
 *  because their delta is already in the subtotal.
 */
export function buildLedgerLineItems(
  cards: ReadonlyArray<WheelActionCard>,
  selfSeatId: number,
  seats: ReadonlyArray<WheelSeatView>,
): CentralLedgerLineItem[] {
  const items: CentralLedgerLineItem[] = [];
  for (const c of cards) {
    if (WEDGE_BAKED_ITEMS.has(c.itemId)) continue;
    if (
      (c.itemId === 'LIGHTNING' || c.itemId === 'POW') &&
      c.targetSeatId !== null
    ) {
      if (c.targetSeatId !== selfSeatId) continue;
    }
    const isSource = c.sourceSeatId === selfSeatId;
    const isTarget = c.targetSeatId === selfSeatId;
    if (!isSource && !isTarget) continue;
    const delta = isSource ? c.sourceDelta : c.targetDelta;
    const note = shakedownNote(c.itemId, delta, c.blocked, isSource && !isTarget);
    if (delta === 0 && !c.blocked && !note) continue;
    const label = lineLabel(c, isSource, isTarget, seats);
    items.push(note ? { label, delta, blocked: c.blocked, note } : { label, delta, blocked: c.blocked });
  }
  return items;
}

/** Render server-emitted {@link WheelLedgerLine}s into the
 *  display-ready {@link CentralLedgerLineItem}s the widget consumes.
 *  Mirrors the client-side {@link buildLedgerLineItems} label rules
 *  (so the two paths produce identical output for the same inputs)
 *  but takes the structured server payload as input — the server
 *  already filtered wedge-baked items and per-rival siblings.
 *
 *  Producer feedback 2026-05-27: <i>"I imagine the final winning
 *  ledger comes from the server."</i>
 */
export function renderServerLedgerLines(
  lines: ReadonlyArray<WheelLedgerLine>,
  seats: ReadonlyArray<WheelSeatView>,
): CentralLedgerLineItem[] {
  return lines.map((l) => {
    const item = { label: serverLineLabel(l, seats), delta: l.delta, blocked: l.blocked };
    const note = shakedownNote(l.itemId, l.delta, l.blocked, l.kind === 'OUTGOING');
    return note ? { ...item, note } : item;
  });
}

function serverLineLabel(
  line: WheelLedgerLine,
  seats: ReadonlyArray<WheelSeatView>,
): string {
  const itemLabel = ITEM_LABEL[line.itemId] ?? line.itemId;
  switch (line.kind) {
    case 'SELF':
      return itemLabel;
    case 'OUTGOING': {
      const targetName = firstName(
        seats.find((s) => s.seatId === line.otherSeatId),
      );
      return line.itemId === 'SHAKEDOWN' ? shookDown(targetName) : `${itemLabel} ${targetName}`;
    }
    case 'INCOMING': {
      const sourceName = firstName(
        seats.find((s) => s.seatId === line.otherSeatId),
      );
      return line.itemId === 'SHAKEDOWN' ? shookYouDown(sourceName) : `${sourceName}'s ${itemLabel}`;
    }
  }
}

/** Shakedown lines say who took from whom (producer 2026-11-03: the
 *  ledger should be clearer than "Shakedown Bea"). */
const shookDown = (targetName: string | null): string => `Shook down ${targetName ?? '?'}`;
const shookYouDown = (sourceName: string): string => `${sourceName} shook you down`;

function firstName(seat: WheelSeatView | undefined): string {
  return seat?.displayName.split(' ')[0] ?? '?';
}

function lineLabel(
  card: WheelActionCard,
  isSource: boolean,
  isTarget: boolean,
  seats: ReadonlyArray<WheelSeatView>,
): string {
  const sourceName = firstName(seats.find((s) => s.seatId === card.sourceSeatId));
  const targetName =
    card.targetSeatId !== null
      ? firstName(seats.find((s) => s.seatId === card.targetSeatId))
      : null;
  const itemLabel = ITEM_LABEL[card.itemId] ?? card.itemId;
  if (isSource && !isTarget) {
    if (card.targetSeatId === null) {
      return itemLabel;
    }
    return card.itemId === 'SHAKEDOWN' ? shookDown(targetName) : `${itemLabel} ${targetName}`;
  }
  return card.itemId === 'SHAKEDOWN' ? shookYouDown(sourceName) : `${sourceName}'s ${itemLabel}`;
}

/** Compute the local player's projected match rank to determine
 *  their podium multiplier. Mirrors the server's PAYOUT-time math
 *  ({@code WheelGameRules.PODIUM_MULTIPLIER_PERCENT} = [100, 50, 20])
 *  so the ledger and the match-wrap show the same number.
 *
 *  Projection = settled tokens = max(0, tokens − pendingStake +
 *  perSeatOutcome.payout) — the same key the server settles on at
 *  PAYOUT, so the ledger's projected rank matches the match-wrap
 *  leaderboard. (payout already includes every item delta.)
 */
export function projectPodiumRank(
  state: WheelObservableState,
  selfSeatId: number,
): 1 | 2 | 3 | null {
  const outcomes = state.dialResolveResult?.perSeatOutcomes ?? {};
  type Projection = { seatId: number; projected: number };
  const projections: Projection[] = [];
  for (const seat of state.seats) {
    if (seat.kind === 'EMPTY') continue;
    const outcome = outcomes[String(seat.seatId)];
    const payout = outcome?.payout ?? 0;
    // Settled tokens = max(0, tokens − stake + payout), matching the
    // server's PAYOUT settlement (WheelGameRules: settled = max(0,
    // tokens − stake + payout)). Earlier this summed tokens + payout,
    // which double-counts the staked bank (default stake = the whole
    // bank) and so disagreed with the match-wrap leaderboard's ranking.
    const stake = seat.pendingStake ?? 0;
    projections.push({
      seatId: seat.seatId,
      projected: Math.max(0, seat.tokens - stake + payout),
    });
  }
  projections.sort((a, b) => {
    if (b.projected !== a.projected) return b.projected - a.projected;
    return a.seatId - b.seatId;
  });
  const idx = projections.findIndex((p) => p.seatId === selfSeatId);
  if (idx === 0) return 1;
  if (idx === 1) return 2;
  if (idx === 2) return 3;
  return null;
}
